import { useEffect, useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, AddressElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const PLAN_LABEL = {
  base: { name: "Standard mensuel", price: "29€ / mois", detail: "Sans engagement" },
  base_yearly: { name: "Standard annuel", price: "26,10€ / mois", detail: "Engagement 12 mois — économie de 10%" },
  premium: { name: "Premium mensuel", price: "49€ / mois", detail: "Sans engagement" },
  premium_yearly: { name: "Premium annuel", price: "44,10€ / mois", detail: "Engagement 12 mois — économie de 10%" },
};

let _stripePromise = null;
async function getStripe() {
  if (_stripePromise) return _stripePromise;
  const { data } = await api.get("/billing/config");
  _stripePromise = loadStripe(data.publishable_key);
  return _stripePromise;
}

const APPEARANCE = {
  theme: "flat",
  variables: {
    colorPrimary: "#3A8FA5",
    colorBackground: "#FFFFFF",
    colorText: "#1E3A5F",
    colorTextSecondary: "#5F758F",
    colorDanger: "#B9502C",
    fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
    borderRadius: "6px",
    spacingUnit: "4px",
  },
  rules: {
    ".Label": { color: "#5F758F", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: "500" },
    ".Input": { border: "1px solid #E1E9F0", backgroundColor: "#FFFFFF", padding: "10px 12px" },
    ".Input:focus": { borderColor: "#3A8FA5", boxShadow: "0 0 0 1px #3A8FA5" },
    ".Tab": { border: "1px solid #E1E9F0", backgroundColor: "#F4F7FB" },
    ".Tab--selected": { borderColor: "#3A8FA5", backgroundColor: "#F9FBFA" },
  },
};

function CheckoutForm({ subscriptionId, plan, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [walletReady, setWalletReady] = useState(false);
  const [terms, setTerms] = useState(false);
  const cfg = PLAN_LABEL[plan] || {};

  const finalizeSuccess = useCallback(async () => {
    try { await api.get(`/billing/subscription/${subscriptionId}`); } catch (err) { log.warn("post-pay confirm", err); }
    toast.success("Paiement confirmé");
    onSuccess?.();
  }, [subscriptionId, onSuccess]);

  const onExpressConfirm = async (event) => {
    if (!stripe || !elements) return;
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: window.location.origin + "/settings?tab=billing" },
    });
    if (error) { setErrorMsg(error.message || "Paiement refusé"); return; }
    if (paymentIntent?.status === "succeeded") await finalizeSuccess();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setErrorMsg(null);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: window.location.origin + "/settings?tab=billing" },
    });
    if (error) {
      setErrorMsg(error.message || "Paiement refusé");
      setBusy(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      await finalizeSuccess();
    } else {
      toast.info("Paiement en cours de traitement…");
      onSuccess?.();
    }
    setBusy(false);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="border border-[#E1E9F0] rounded-md p-4 bg-[#F4F7FB]/50 flex items-start gap-3">
        <ShieldCheck size={22} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-medium text-[#1E3A5F]">{cfg.name}</div>
          <div className="text-[#5F758F]">{cfg.price} · {cfg.detail}</div>
        </div>
      </div>

      {/* Express wallets (Apple Pay / Google Pay / Link) — only rendered if the device supports them. */}
      <div className={walletReady ? "" : "hidden"}>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-2">Paiement rapide</div>
        <ExpressCheckoutElement
          onReady={({ availablePaymentMethods }) => setWalletReady(!!availablePaymentMethods && Object.keys(availablePaymentMethods).length > 0)}
          onConfirm={onExpressConfirm}
          options={{ buttonHeight: 44 }}
        />
        <div className="relative my-4 flex items-center gap-2 text-xs text-[#5F758F]">
          <div className="flex-1 border-t border-[#E1E9F0]"></div>
          <span>ou</span>
          <div className="flex-1 border-t border-[#E1E9F0]"></div>
        </div>
      </div>

      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Adresse de facturation</div>
        <AddressElement options={{ mode: "billing", display: { name: "organization" }, fields: { phone: "auto" } }} />
      </div>

      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Carte bancaire</div>
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      {errorMsg && (
        <div className="text-sm text-[#B9502C] bg-[#FBE8E1] border border-[#B9502C]/20 rounded p-2">{errorMsg}</div>
      )}
      <label className="flex items-start gap-2.5 text-xs text-[#5F758F] leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          checked={terms}
          onChange={(e) => setTerms(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-[#C7DEE6] text-[#3A8FA5] focus:ring-[#3A8FA5]"
          data-testid="checkout-terms"
        />
        <span>
          J'accepte les{" "}
          <a href="/cgv" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGV</a>,{" "}
          les{" "}
          <a href="/cgu" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGU</a>{" "}
          et la{" "}
          <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">politique de confidentialité</a>{" "}
          de Questiomed.
        </span>
      </label>
      <Button
        type="submit"
        disabled={!stripe || busy || !terms}
        className="w-full rounded-md bg-[#3A8FA5] hover:bg-[#164E5F] text-white h-11 transition-colors disabled:opacity-50"
        data-testid="whitelabel-pay-btn"
      >
        <Lock size={16} className="mr-2" /> {busy ? "Confirmation…" : "Confirmer et payer"}
      </Button>
      <p className="text-xs text-[#5F758F] text-center">
        Paiement sécurisé — vos coordonnées bancaires ne transitent jamais par Questiomed.
      </p>
    </form>
  );
}

export default function WhiteLabelCheckoutDialog({ open, plan, onOpenChange, onCompleted }) {
  const [stripe, setStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | success | error

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setSubscriptionId(null);
      setState("loading");
      return;
    }
    (async () => {
      try {
        setState("loading");
        setStripe(await getStripe());
        const { data } = await api.post("/billing/subscribe", { plan });
        setClientSecret(data.client_secret);
        setSubscriptionId(data.subscription_id);
        setState("ready");
      } catch (err) {
        log.error("subscribe init", err);
        toast.error(err?.response?.data?.detail || "Impossible d'initialiser le paiement");
        setState("error");
        onOpenChange(false);
      }
    })();
  }, [open, plan, onOpenChange]);

  const handleSuccess = useCallback(() => {
    setState("success");
    onCompleted?.();
    setTimeout(() => onOpenChange(false), 1400);
  }, [onCompleted, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F]">Finaliser l'abonnement</DialogTitle>
          <DialogDescription className="text-sm text-[#5F758F]">
            Paiement sécurisé — 100% intégré à Questiomed.
          </DialogDescription>
        </DialogHeader>

        {state === "loading" && (
          <div className="flex items-center justify-center py-12">
            <div className="flex gap-2">
              <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot"></span>
              <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.15s" }}></span>
              <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.3s" }}></span>
            </div>
          </div>
        )}

        {state === "success" && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle size={48} weight="duotone" className="text-[#3A8FA5] mb-3" />
            <p className="text-xl font-serif text-[#3A8FA5]">Paiement confirmé</p>
          </div>
        )}

        {state === "ready" && stripe && clientSecret && (
          <Elements stripe={stripe} options={{ clientSecret, appearance: APPEARANCE }}>
            <CheckoutForm subscriptionId={subscriptionId} plan={plan} onSuccess={handleSuccess} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}
